import React, { useState, useEffect, useRef } from 'react';

const Resume: React.FC = () => {
    const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    useEffect(() => {
        const observers = Object.entries(sectionRefs.current).map(([key, element]) => {
            if (!element) return null;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setVisibleSections((prev) => new Set(prev).add(key));
                    }
                },
                { threshold: 0.1 }
            );

            observer.observe(element);
            return observer;
        });

        return () => {
            observers.forEach((observer) => observer?.disconnect());
        };
    }, []);

    return (
        <section id="resume" className="bg-gray-50 py-16 md:py-24 px-4 sm:px-8 md:px-12 lg:px-24">
            <div className="max-w-7xl mx-auto">
                
                {/* Section Header */}
                <div 
                    ref={(el) => { sectionRefs.current['header'] = el; }}
                    className={`mb-12 transition-all duration-1000 ${
                        visibleSections.has('header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                        Resume
                        </h2>

                        {/* Reflection */}
                        <h2
                            className="
                                text-4xl md:text-5xl font-bold
                                transform scale-y-[-1]
                                opacity-40
                                bg-[linear-gradient(to_top,rgba(17,24,39,3)_0%,rgba(17,24,39,0.4)_40%,rgba(17,24,39,0.05)_70%,rgba(17,24,39,0)_100%)]
                                bg-clip-text text-transparent
                                pointer-events-none
                            "
                            >
                        Resume
                    </h2>

                    <p className="text-gray-600 leading-relaxed max-w-3xl">
                        A comprehensive overview of my professional journey, technical expertise, 
                        and educational background.
                    </p>
                    <a
                        href="/Lawrence_Babelonia_Resume.pdf"
                        download="Lawrence_Babelonia_Resume.pdf"
                        className="inline-flex items-center mt-4 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Resume
                    </a>
                </div>

                {/* Work Experience Section */}
                <div 
                    ref={(el) => { sectionRefs.current['work'] = el; }}
                    className={`mb-16 transition-all duration-1000 delay-200 ${
                        visibleSections.has('work') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                        <span className="w-2 h-8 bg-blue-600 mr-3"></span>
                        Work Experience
                    </h3>

                    <div className="space-y-8">
                        {/* Job 1 */}
                        <div className="relative pl-8 border-l-2 border-gray-300">
                            <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 rounded-full"></div>
                            <div className="mb-1 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <h4 className="text-xl font-semibold text-gray-900">Full-Stack Software Developer</h4>
                                <span className="text-sm text-gray-500 mt-1 sm:mt-0">September 2025 - Present</span>
                            </div>
                            <p className="text-blue-600 font-medium mb-3">Nexvision Innovations Inc. (BGC)</p>
                            <ul className="text-gray-600 text-sm space-y-2 list-disc list-inside">
                                <li>Led development of responsive web applications using React, TypeScript, and Tailwind CSS</li>
                                <li>Collaborated with senior developers and designers to translate UI designs into functional, responsive web pages</li>
                                <li>Gained hands-on experience working with real client requirements, deployment environments, and version control workflows</li>
                                <li>Mentored junior developers and conducted code reviews</li>
                                <li>Supported WordPress-based projects through custom theme development and feature enhancements</li>
                                <li>Assisted in testing, debugging, and maintaining production websites</li>
                            </ul>

                            <p className="text-gray-700 font-semibold text-sm mt-4 mb-2">DevOps & Infrastructure</p>
                            <ul className="text-gray-600 text-sm space-y-2 list-disc list-inside">
                                <li>Provisioned and managed Linux-based VPS servers on Contabo and GreenGeeks for hosting production applications</li>
                                <li>Configured and maintained web server stacks using aaPanel, Nginx, MariaDB, and PHP-FPM</li>
                                <li>Managed hosting environments via cPanel and phpMyAdmin for WordPress and PHP-based projects</li>
                                <li>Built CI/CD pipelines using GitHub Actions for automated testing, builds, and deployments</li>
                                <li>Implemented server security hardening with UFW firewall rules, Fail2Ban intrusion prevention, and secure SSH configurations</li>
                                <li>Set up and maintained mail server infrastructure using Postfix, Dovecot, and Rspamd for spam filtering</li>
                                <li>Managed DNS records, SSL certificate provisioning, and domain configurations</li>
                                <li>Automated server backups and scheduled maintenance tasks using Cron jobs</li>
                            </ul>
                        </div>

                        {/* Job 2 */}
                        <div className="relative pl-8 border-l-2 border-gray-300">
                            <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 rounded-full"></div>
                            <div className="mb-1 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <h4 className="text-xl font-semibold text-gray-900">Junior Full-Stack Developer Intern</h4>
                                <span className="text-sm text-gray-500 mt-1 sm:mt-0">March 2025 - June 2025</span>
                            </div>
                            <p className="text-blue-600 font-medium mb-3">Alpha Premier Group of Companies OPC (Ortigas)</p>
                            <ul className="text-gray-600 text-sm space-y-2 list-disc list-inside">
                                <li>Contributed to the development and maintenance of client websites using HTML, CSS, JavaScript, PHP, and MySQL</li>
                                <li>Implemented backend features such as form handling, data validation, and database integration</li>
                                <li>Performed basic performance optimizations and bug fixes to improve site stability and user experience</li>
                                <li>Developed and maintained backend logic for client systems, including CRUD operations and data processing</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Education Section */}
                <div 
                    ref={(el) => { sectionRefs.current['education'] = el; }}
                    className={`mb-16 transition-all duration-1000 delay-400 ${
                        visibleSections.has('education') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                        <span className="w-2 h-8 bg-blue-600 mr-3"></span>
                        Education
                    </h3>

                    <div className="space-y-6">
                        {/* College */}
                         <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                                <h4 className="text-xl font-semibold text-gray-900">Bachelor of Science in Computer Science</h4>
                                <span className="text-sm text-gray-500 mt-1 sm:mt-0">2021 - 2025</span>
                            </div>
                            <p className="text-blue-600 font-medium mb-2">Eulogio "Amang" Rodriguez Institute of Science and Technology</p>
                        </div>

                        {/* Senior High School */}
                         <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                                <h4 className="text-xl font-semibold text-gray-900">Senior High School - ICT Strand</h4>
                                <span className="text-sm text-gray-500 mt-1 sm:mt-0">2018 - 2020</span>
                            </div>
                            <p className="text-blue-600 font-medium mb-2">The National Teachers College</p>
                        </div>


                    </div>
                </div>

                {/* Skills Section */}
                <div 
                    ref={(el) => { sectionRefs.current['skills'] = el; }}
                    className={`transition-all duration-1000 delay-600 ${
                        visibleSections.has('skills') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                >
                    <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                        <span className="w-2 h-8 bg-blue-600 mr-3"></span>
                        Tech Stack
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Frontend Development */}
                        <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Frontend Development</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">JavaScript</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">TypeScript</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">React.js</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Vite</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">React Native</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Tailwind CSS</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Material UI</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">HTML5</span>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">CSS3</span>
                            </div>
                        </div>

                        {/* Backend Development */}
                        <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Backend Development</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Node.js</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Express.js</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">C#</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">C++</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">PHP</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Python</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">MySQL</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">MongoDB</span>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Supabase</span>
                            </div>
                        </div>

                        {/* Design & Tools */}
                        <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Design & Tools</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Figma</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">WordPress</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Elementor</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Git</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Vercel</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Netlify</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Blender</span>
                            </div>
                        </div>

                        {/* Other Technologies */}
                        <div className="space-y-2 bg-white shadow-lg rounded-md p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Other Technologies</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">Unity</span>
                                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">Machine Learning</span>
                                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">SEO</span>
                            </div>
                        </div>

                        {/* DevOps & Infrastructure */}
                        <div className="space-y-2 bg-white shadow-lg rounded-md p-6 md:col-span-2">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">DevOps & Infrastructure</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Linux</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">VPS Hosting</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Contabo</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">GreenGeeks</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">aaPanel</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Nginx</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">MariaDB</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">PHP-FPM</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">cPanel</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">phpMyAdmin</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">GitHub Actions</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">CI/CD</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">UFW</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Fail2Ban</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">SSH</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Cron</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Postfix</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Dovecot</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Rspamd</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">DNS Management</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">SSL</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Backup Automation</span>
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Server Security Hardening</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    )
}

export default Resume